// components/OutlookInbox.js - Load More (Infinite Scroll) Version
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useHistory } from "react-router-dom";
import {
  Button,
  IconButton,
  Checkbox,
  TextField,
  Snackbar,
  Menu,
  MenuItem,
  Divider,
  Badge,
} from "@material-ui/core";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteIcon from "@mui/icons-material/Delete";
import MarkunreadIcon from "@mui/icons-material/Markunread";
import SendIcon from "@mui/icons-material/Send";
import ReplyIcon from "@mui/icons-material/Reply";
import ReplyAllIcon from "@mui/icons-material/ReplyAll";
import InboxIcon from "@mui/icons-material/Inbox";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import DraftsOutlinedIcon from "@mui/icons-material/DraftsOutlined";
import ArchiveIcon from "@mui/icons-material/Archive";
import ReportIcon from "@mui/icons-material/Report";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import Dashboard from "../Dashbard";
import Loader from "../../../../components/common/Loader";
import { Alert } from "@mui/material";
import "./OutlookInbox.css";
import { dashboardActions } from "../../../../store/dashboard/dashboardActions";
import { useDispatch } from "react-redux";

// Import separate components
import EmailComposer from "./EmailComposer";
import EmailDetail from "./EmailDetail";
import EmailList from "./EmailList";

function OutlookInbox() {
  const history = useHistory();
  const dispatch = useDispatch();
  const [emails, setEmails] = useState([]);
  const [filteredEmails, setFilteredEmails] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [showComposer, setShowComposer] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeFolder, setActiveFolder] = useState("inbox");
  const [composerMode, setComposerMode] = useState("new");
  const [viewMode, setViewMode] = useState("list");

  // Pagination states for Load More
  const [nextCursorDate, setNextCursorDate] = useState(null);
  const [nextCursorId, setNextCursorId] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [currentPageNum, setCurrentPageNum] = useState(1);

  // Ref for intersection observer
  const observerRef = useRef();
  const loadMoreTriggerRef = useRef();

  const folders = useMemo(
    () => [
      { id: "inbox", name: "Inbox", icon: <InboxIcon /> },
      { id: "sent", name: "Sent", icon: <SendOutlinedIcon /> },
      { id: "drafts", name: "Drafts", icon: <DraftsOutlinedIcon /> },
      { id: "archive", name: "Archive", icon: <ArchiveIcon /> },
      { id: "spam", name: "Spam", icon: <ReportIcon /> },
      { id: "trash", name: "Trash", icon: <DeleteOutlinedIcon /> },
    ],
    [],
  );

  const formatEmailData = useCallback(apiData => {
    if (!apiData || !Array.isArray(apiData)) return [];

    return apiData.map((email, index) => ({
      id: email.id || email.messageId || `${Date.now()}_${index}`,
      messageId: email.messageId,
      conversationId: email.conversationId,
      from: email.from || "",
      fromName: extractNameFromEmail(email.from) || "Unknown Sender",
      to: email.toRecipients
        ? email.toRecipients.split(",").map(e => e.trim())
        : [],
      subject: email.subject || "No Subject",
      preview: email.bodyPreview || "No preview available",
      timestamp:
        email.dateTimeReceived ||
        email.dateTimeSent ||
        new Date().toISOString(),
      isRead: email.isRead || false,
      hasAttachment: email.hasAttachments || false,
      attachments: email.attachments || email.attachemnts || [],
      isStarred: false,
      isImportant: false,
      labels: [],
      body: email.body || "<p>No content</p>",
      cc: email.ccRecipients
        ? email.ccRecipients.split(",").map(e => e.trim())
        : [],
      bcc: [],
      originalData: email,
    }));
  }, []);

  const extractNameFromEmail = email => {
    if (!email) return "Unknown";
    const match = email.match(/(.*?)@/);
    return match
      ? match[1].replace(/\./g, " ").replace(/\b\w/g, l => l.toUpperCase())
      : email;
  };

  // Initial fetch or refresh
  const fetchInitialEmails = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const userId = localStorage.getItem("id");
      const response = await dispatch(
        dashboardActions.onGetEmail(userId, "", "", "", 1),
      );

      console.log("Initial fetch response:", response?.data?.data);

      if (response?.data?.success && Array.isArray(response.data.data.data)) {
        const formattedEmails = formatEmailData(response.data.data.data);
        setEmails(formattedEmails);
        setFilteredEmails(formattedEmails);

        // Set cursor for next page
        const nextDate = response.data.data.nextCursorDate || null;
        const nextId = response.data.data.nextCursorId || null;

        setNextCursorDate(nextDate);
        setNextCursorId(nextId);
        setHasMore(!!(nextDate && nextId) && formattedEmails.length === 10);
        setCurrentPageNum(1);

        let loadMessage = `Loaded ${formattedEmails.length} emails`;
        if (formattedEmails.length === 0) {
          loadMessage = "No emails found";
        }

        setSnackbar({
          open: true,
          message: loadMessage,
          severity: "success",
        });
      } else {
        console.error("Failed to load emails:", response?.data?.message);
        setSnackbar({
          open: true,
          message: "Failed to load emails",
          severity: "error",
        });
        setEmails([]);
        setFilteredEmails([]);
        setHasMore(false);
      }
    } catch (err) {
      console.error("Error fetching emails:", err);
      setError(err.message);
      setSnackbar({
        open: true,
        message: "Failed to load emails",
        severity: "error",
      });
      setEmails([]);
      setFilteredEmails([]);
    } finally {
      setLoading(false);
    }
  }, [dispatch, formatEmailData]);

  // Load more emails (append to existing list)
  const loadMoreEmails = useCallback(async () => {
    if (loadingMore || !hasMore || !nextCursorDate || !nextCursorId) {
      console.log("Load more blocked:", {
        loadingMore,
        hasMore,
        nextCursorDate,
        nextCursorId,
      });
      return;
    }

    try {
      setLoadingMore(true);
      console.log("Loading more emails with cursor:", {
        nextCursorDate,
        nextCursorId,
      });

      const userId = localStorage.getItem("id");
      const nextPageNum = currentPageNum + 1;

      const response = await dispatch(
        dashboardActions.onGetEmail(
          userId,
          nextCursorDate,
          nextCursorId,
          "next",
          nextPageNum,
        ),
      );

      console.log("Load more response:", response?.data?.data);

      if (response?.data?.success && Array.isArray(response.data.data.data)) {
        const newFormattedEmails = formatEmailData(response.data.data.data);

        // Append new emails to existing list
        setEmails(prevEmails => [...prevEmails, ...newFormattedEmails]);
        setFilteredEmails(prevEmails => [...prevEmails, ...newFormattedEmails]);

        // Update cursor for next page
        const nextDate = response.data.data.nextCursorDate || null;
        const nextId = response.data.data.nextCursorId || null;

        setNextCursorDate(nextDate);
        setNextCursorId(nextId);
        setHasMore(!!(nextDate && nextId) && newFormattedEmails.length === 10);
        setCurrentPageNum(nextPageNum);

        console.log(
          `Loaded ${newFormattedEmails.length} more emails. Total: ${emails.length + newFormattedEmails.length}`,
        );
      } else {
        console.error("Failed to load more emails:", response?.data?.message);
        setHasMore(false);
        setSnackbar({
          open: true,
          message: "No more emails to load",
          severity: "info",
        });
      }
    } catch (err) {
      console.error("Error loading more emails:", err);
      setSnackbar({
        open: true,
        message: "Failed to load more emails",
        severity: "error",
      });
    } finally {
      setLoadingMore(false);
    }
  }, [
    dispatch,
    formatEmailData,
    loadingMore,
    hasMore,
    nextCursorDate,
    nextCursorId,
    currentPageNum,
    emails.length,
  ]);

  // Setup Intersection Observer for infinite scroll
  useEffect(() => {
    if (loading || loadingMore || !hasMore) return;

    const options = {
      root: null,
      rootMargin: "0px 0px 100px 0px", // Load when 100px before bottom
      threshold: 0.1,
    };

    const observer = new IntersectionObserver(entries => {
      const target = entries[0];
      if (target.isIntersecting && hasMore && !loadingMore) {
        console.log("Load more triggered by scroll");
        loadMoreEmails();
      }
    }, options);

    const currentTrigger = loadMoreTriggerRef.current;
    if (currentTrigger) {
      observer.observe(currentTrigger);
    }

    return () => {
      if (currentTrigger) {
        observer.unobserve(currentTrigger);
      }
    };
  }, [loading, loadingMore, hasMore, loadMoreEmails]);

  // Initial load
  useEffect(() => {
    fetchInitialEmails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter emails based on search term (optional)
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredEmails(emails);
    } else {
      const filtered = emails.filter(
        email =>
          email.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
          email.fromName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          email.preview.toLowerCase().includes(searchTerm.toLowerCase()),
      );
      setFilteredEmails(filtered);
    }
  }, [searchTerm, emails]);

  const handleEmailClick = useCallback(
    email => {
      setSelectedEmail(email);
      setViewMode("detail");
      if (!email.isRead) {
        setEmails(
          emails.map(e => (e.id === email.id ? { ...e, isRead: true } : e)),
        );
      }
    },
    [emails],
  );

  const handleBackToList = useCallback(() => {
    setViewMode("list");
    setSelectedEmail(null);
  }, []);

  const handleSelectEmail = useCallback((emailId, e) => {
    e.stopPropagation();
    setSelectedEmails(prev =>
      prev.includes(emailId)
        ? prev.filter(id => id !== emailId)
        : [...prev, emailId],
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedEmails(prev =>
      prev.length === filteredEmails.length
        ? []
        : filteredEmails.map(email => email.id),
    );
  }, [filteredEmails]);

  const handleMarkAsRead = useCallback(() => {
    setEmails(
      emails.map(email =>
        selectedEmails.includes(email.id) ? { ...email, isRead: true } : email,
      ),
    );
    setSelectedEmails([]);
    setSnackbar({
      open: true,
      message: "Marked as read",
      severity: "success",
    });
  }, [emails, selectedEmails]);

  const handleMarkAsUnread = useCallback(() => {
    setEmails(
      emails.map(email =>
        selectedEmails.includes(email.id) ? { ...email, isRead: false } : email,
      ),
    );
    setSelectedEmails([]);
    setSnackbar({
      open: true,
      message: "Marked as unread",
      severity: "success",
    });
  }, [emails, selectedEmails]);

  const handleDelete = useCallback(() => {
    if (selectedEmails.length === 0) return;

    if (
      window.confirm(
        `Are you sure you want to delete ${selectedEmails.length} email(s)?`,
      )
    ) {
      setEmails(emails.filter(email => !selectedEmails.includes(email.id)));
      setSelectedEmails([]);
      setSnackbar({
        open: true,
        message: "Emails moved to trash",
        severity: "success",
      });
    }
  }, [emails, selectedEmails]);

  const handleStarToggle = useCallback(
    (emailId, e) => {
      e.stopPropagation();
      setEmails(
        emails.map(email =>
          email.id === emailId
            ? { ...email, isStarred: !email.isStarred }
            : email,
        ),
      );
    },
    [emails],
  );

  const handleMenuClick = useCallback(event => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleReply = useCallback(
    mode => {
      setComposerMode(mode);
      setShowComposer(true);
      handleMenuClose();
    },
    [handleMenuClose],
  );

  const handleSendEmail = useCallback(
    async emailData => {
      try {
        setSnackbar({
          open: true,
          message: "Sending email...",
          severity: "info",
        });

        const payload = {
          messageId: emailData.messageId || 0,
          body: emailData.body,
          subject: emailData.subject,
          toEmails: emailData.toEmails,
          replyAll: emailData.replyAll || false,
        };

        const response = await dispatch(dashboardActions.onSendEmail(payload));
        if (response?.data?.success) {
          setSnackbar({
            open: true,
            message: "Email sent successfully",
            severity: "success",
          });
          // Refresh inbox after sending
          fetchInitialEmails();
          return true;
        } else {
          setSnackbar({
            open: true,
            message: response?.data?.message || "Failed to send email",
            severity: "error",
          });
          return false;
        }
      } catch (error) {
        console.error("Failed to send email:", error);
        setSnackbar({
          open: true,
          message: "Failed to send email",
          severity: "error",
        });
        return false;
      }
    },
    [dispatch, fetchInitialEmails],
  );

  const inboxUnreadCount = useMemo(
    () => emails.filter(email => !email.isRead).length,
    [emails],
  );

  if (loading) {
    return (
      <Dashboard>
        <div className='outlook-inbox-container'>
          <Loader />
        </div>
      </Dashboard>
    );
  }

  return (
    <Dashboard>
      <div className='outlook-inbox-container'>
        {/* Main Header */}
        <div className='inbox-main-header'>
          {viewMode === "detail" ? (
            <div className='detail-header'>
              <IconButton
                onClick={handleBackToList}
                className='back-button'
                size='medium'>
                <ArrowBackIcon />
              </IconButton>
              <div className='detail-header-title'>
                <h1>Message</h1>
                <span className='email-subject-header'>
                  {selectedEmail?.subject}
                </span>
              </div>
            </div>
          ) : (
            <>
              <div className='inbox-title'>
                <h1>Outlook Inbox</h1>
              </div>
              <div className='header-actions'>
                <Button
                  variant='contained'
                  color='primary'
                  startIcon={<SendIcon />}
                  onClick={() => {
                    setComposerMode("new");
                    setShowComposer(true);
                  }}
                  className='compose-button'
                  style={{ textTransform: "none" }}>
                  Compose
                </Button>
                <IconButton onClick={fetchInitialEmails} title='Refresh'>
                  <RefreshIcon />
                </IconButton>
              </div>
            </>
          )}
        </div>

        {/* Main Content */}
        {viewMode === "list" ? (
          <div className='inbox-layout'>
            {/* Left Sidebar */}
            <div className='inbox-sidebar'>
              <Button
                variant='contained'
                color='primary'
                startIcon={<SendIcon />}
                onClick={() => {
                  setComposerMode("new");
                  setShowComposer(true);
                }}
                className='sidebar-compose'
                fullWidth
                style={{ textTransform: "none", marginBottom: "16px" }}>
                Compose
              </Button>

              <div className='folders-list'>
                {folders.map(folder => {
                  const isInbox = folder.id === "inbox";
                  const showBadge = isInbox && inboxUnreadCount > 0;

                  return (
                    <Button
                      key={folder.id}
                      startIcon={folder.icon}
                      className={`folder-item ${
                        activeFolder === folder.id ? "active" : ""
                      }`}
                      onClick={() => setActiveFolder(folder.id)}
                      fullWidth
                      style={{
                        textTransform: "none",
                        justifyContent: "flex-start",
                        position: "relative",
                      }}>
                      <span className='folder-name'>{folder.name}</span>
                      {showBadge && (
                        <Badge
                          badgeContent={inboxUnreadCount}
                          color='primary'
                          max={99}
                          style={{
                            position: "absolute",
                            right: "16px",
                            top: "50%",
                            transform: "translateY(-50%)",
                          }}
                        />
                      )}
                    </Button>
                  );
                })}
              </div>
            </div>

            {/* Email List Container with Infinite Scroll */}
            <div className='email-list-container'>
              <EmailList
                emails={filteredEmails}
                selectedEmails={selectedEmails}
                selectedEmail={selectedEmail}
                onEmailClick={handleEmailClick}
                onSelectEmail={handleSelectEmail}
                onSelectAll={handleSelectAll}
                onStarToggle={handleStarToggle}
                onMarkAsRead={handleMarkAsRead}
                onMarkAsUnread={handleMarkAsUnread}
                onDelete={handleDelete}
                searchTerm={searchTerm}
              />

              {/* Load More Trigger Element */}
              {hasMore && (
                <div ref={loadMoreTriggerRef} className='load-more-trigger'>
                  {loadingMore ? (
                    <div className='load-more-loader'>
                      <Loader />
                      <span>Loading more emails...</span>
                    </div>
                  ) : (
                    <div className='load-more-placeholder'>Scroll for more</div>
                  )}
                </div>
              )}

              {/* No More Emails Message */}
              {!hasMore && emails.length > 0 && (
                <div className='no-more-emails'>
                  <Divider />
                  <p>You've reached the end</p>
                </div>
              )}

              {/* Empty State */}
              {emails.length === 0 && !loading && (
                <div className='empty-inbox'>
                  <InboxIcon style={{ fontSize: 64, color: "#ccc" }} />
                  <h3>No emails found</h3>
                  <p>Your inbox is empty</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Email Detail View */
          <EmailDetail
            email={selectedEmail}
            onBack={handleBackToList}
            onReply={handleReply}
            onMenuClick={handleMenuClick}
          />
        )}

        {/* Email Composer Dialog */}
        <EmailComposer
          open={showComposer}
          onClose={() => {
            setShowComposer(false);
            setComposerMode("new");
          }}
          initialData={getComposerInitialData(selectedEmail, composerMode)}
          mode={composerMode}
          onSend={handleSendEmail}
          selectedEmail={selectedEmail}
        />

        {/* Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          className='email-menu'>
          <MenuItem onClick={() => handleReply("reply")}>
            <ReplyIcon fontSize='small' />
            <span>Reply</span>
          </MenuItem>
          <MenuItem onClick={() => handleReply("replyAll")}>
            <ReplyAllIcon fontSize='small' />
            <span>Reply all</span>
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleMarkAsUnread}>
            <MarkunreadIcon fontSize='small' />
            <span>Mark as unread</span>
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleDelete} style={{ color: "#d32f2f" }}>
            <DeleteIcon fontSize='small' />
            <span>Delete</span>
          </MenuItem>
        </Menu>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            variant='filled'
            sx={{ width: "100%" }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    </Dashboard>
  );
}

const getComposerInitialData = (selectedEmail, mode) => {
  let initialData = {
    to: "",
    subject: "",
    body: "",
  };

  if (selectedEmail && mode) {
    switch (mode) {
      case "reply":
        initialData = {
          to: selectedEmail.from,
          subject: `Re: ${selectedEmail.subject}`,
          body: `<br><br><blockquote>${selectedEmail.body}</blockquote>`,
        };
        break;
      case "replyAll":
        const allRecipients = [
          selectedEmail.from,
          ...(selectedEmail.cc || []),
          ...(selectedEmail.bcc || []),
        ].filter(recipient => recipient && recipient !== "me@example.com");

        const uniqueRecipients = allRecipients.filter(
          (recipient, index) => allRecipients.indexOf(recipient) === index,
        );

        initialData = {
          to: uniqueRecipients.join(", "),
          subject: `Re: ${selectedEmail.subject}`,
          body: `<br><br><blockquote>${selectedEmail.body}</blockquote>`,
        };
        break;
      default:
        break;
    }
  }

  return initialData;
};

export default OutlookInbox;
